import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { NewsRow } from "@/components/feature/NewsCard";
import { Toast, copyToClipboard, useToast } from "@/components/base/Toast";
import { newsList } from "@/mocks/news";
import { works } from "@/mocks/works";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const index = newsList.findIndex((item) => item.id === id);
  const article = index >= 0 ? newsList[index] : undefined;
  const { message, show } = useToast();

  usePageMeta(
    article ? `${article.title} - 大桥未久影视资讯` : "资讯详情 - 大桥未久影视资讯",
    article
      ? `${article.summary} 发布时间 ${article.date}，分类：${article.category}。`
      : "查看大桥未久影视资讯详情。",
    article
      ? `${article.category},大桥未久,影视资讯,${article.title}`
      : "大桥未久,影视资讯",
  );

  const handleShare = async () => {
    const success = await copyToClipboard(window.location.href);
    show(success ? "资讯链接已复制，可分享给好友" : "复制失败，请手动复制地址栏链接");
  };

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-newspaper-line text-3xl"></i>
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">资讯不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">
            该条资讯可能已被移除，请返回资讯中心浏览其他内容。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯中心
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = newsList
    .filter((item) => item.id !== article.id)
    .sort((a, b) => {
      const scoreA = a.category === article.category ? 1 : 0;
      const scoreB = b.category === article.category ? 1 : 0;
      if (scoreA !== scoreB) return scoreB - scoreA;
      return b.date.localeCompare(a.date);
    })
    .slice(0, 4);

  const relatedWorks = works.filter((item) => item.status === "new").slice(0, 2);

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6 md:py-6">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: "资讯", to: "/news" },
              { label: article.title },
            ]}
          />
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            <span className="inline-flex items-center rounded bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-800">
              {article.category}
            </span>

            <h1 className="mt-4 text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-[34px] md:leading-[1.35]">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span className="font-label">{article.date}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                <span className="font-label">
                  {article.views.toLocaleString("zh-CN")} 次阅读
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                本站编辑部
              </span>
              <button
                type="button"
                onClick={handleShare}
                className="ml-auto inline-flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                <i className="ri-share-line"></i>
                复制链接
              </button>
            </div>

            <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} 资讯配图`}
                className="h-[220px] w-full object-cover object-top md:h-[380px]"
              />
            </div>

            <p className="mt-6 border-l-2 border-accent-400 pl-4 text-sm leading-relaxed text-foreground-700">
              {article.summary}
            </p>

            <div className="mt-6 space-y-5 text-[15px] leading-[2] text-foreground-700">
              {article.content.map((paragraph, paragraphIndex) => (
                <p key={paragraphIndex}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-8 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              说明：本站资讯为演示内容，相关作品、人物与数据均为虚构示例，不构成任何事实陈述。
              本站不提供播放、下载与付费服务。
            </div>

            <div className="mt-8 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <Link
                to="/news"
                className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-accent-700"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯中心
              </Link>
              <div className="flex flex-col gap-2 sm:flex-row">
                {index > 0 && (
                  <Link
                    to={`/news/${newsList[index - 1].id}`}
                    className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    上一篇
                  </Link>
                )}
                {index < newsList.length - 1 && (
                  <Link
                    to={`/news/${newsList[index + 1].id}`}
                    className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    下一篇
                    <i className="ri-arrow-right-s-line"></i>
                  </Link>
                )}
              </div>
            </div>
          </article>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                <span className="h-3.5 w-1 rounded-full bg-accent-500"></span>
                相关资讯
              </h2>
              <div className="mt-3">
                {related.slice(0, 3).map((item) => (
                  <NewsRow key={item.id} item={item} showCover={false} />
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-primary-50 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">相关作品</h2>
              <div className="mt-4 space-y-3">
                {relatedWorks.map((work) => (
                  <Link
                    key={work.id}
                    to={`/detail/${work.id}`}
                    className="group flex cursor-pointer items-center gap-3 rounded-md border border-primary-200 bg-background-50 p-2.5 transition-colors hover:border-accent-400"
                  >
                    <div className="h-[64px] w-[48px] shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={work.cover}
                        alt={`${work.title} 封面`}
                        title={`${work.title} 相关作品`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-accent-700">
                        {work.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-500">
                        {work.year} · {work.type === "movie" ? "电影" : "电视剧"}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <Link
                to="/category/tv"
                className="mt-4 flex min-h-[44px] cursor-pointer items-center justify-between rounded-md border border-primary-200 bg-background-50 px-3.5 text-sm text-primary-700 transition-colors hover:border-accent-400"
              >
                查看全部作品
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <Toast message={message} />
    </SiteLayout>
  );
}